import _ from 'lodash';
import type { ResourceType } from '@cotera/api';
import { collectTemplateRefs, containsTemplateRef } from '@cotera/utilities';
import type { JsonValue } from '../serializable';
import { Lazy } from '../chat/lazy';
import { fromIsoString, toIsoString } from '../chat/iso-date';
import type { WorkflowNodeData } from './types';
import type { SerializedWorkflowNode } from './types';
import { WorkflowNodeType } from './types';
import type { Workflow } from './workflow';
import type { WorkflowNodeOutputShapeContext } from './workflow-node-output-shape';
import { outputShapeFromDefaultOutput } from './workflow-nodes/output-shape-helpers';

const { startCase } = _;

export const WORKFLOW_RUN_INPUT_NODE_TYPE = 'system/run-input';

function humanizeNodeType(type: string): string {
  if (type === WORKFLOW_RUN_INPUT_NODE_TYPE) {
    return 'Run input';
  }
  const base = type.includes('/') ? type.slice(type.indexOf('/') + 1) : type;
  return startCase(base);
}

export type WorkflowNodeHydration = {
  readonly loadWorkflow: () => Promise<Workflow | null>;
  readonly preloadedWorkflow?: Workflow | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isJsonObject(value: JsonValue): value is { [key: string]: JsonValue } {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** A resource a node's data points at, and the data field that holds its id. */
export type WorkflowNodeResourceRef = {
  readonly type: ResourceType.Agent | ResourceType.Workflow;
  readonly id: string;
  readonly field: string;
};

/** An upstream `sourceId/pointer` ref a node reads, and where the node wrote it. */
export type WorkflowNodeUpstreamRef = {
  /** What an issue calls it: a binding key, or a dotted path into `params`. */
  readonly paramKey: string;
  readonly ref: string;
  /** A `{{ref}}` token inside a string rather than a whole-value binding. */
  readonly template: boolean;
};

/**
 * A node in a workflow graph (`workflow_nodes`).
 */
export class WorkflowNode {
  protected readonly _data: WorkflowNodeData;
  readonly workflow: Lazy<Workflow | null>;

  protected constructor(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ) {
    this._data = data;
    this.workflow = new Lazy(
      hydration.loadWorkflow,
      hydration.preloadedWorkflow
    );
  }

  static create(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ): WorkflowNode {
    return new WorkflowNode(data, hydration);
  }

  get id(): string {
    return this._data.id;
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get ownerOrgId(): string {
    return this._data.ownerOrgId;
  }

  get workflowId(): string {
    return this._data.workflowId;
  }

  get type(): WorkflowNodeType {
    return this._data.type;
  }

  get data(): unknown {
    return this._data.data;
  }

  get inputShape(): string | null {
    return this._data.inputShape;
  }

  get defaultOutput(): unknown {
    return this._data.defaultOutput;
  }

  get inputFromNodeId(): string | null {
    return this._data.inputFromNodeId;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get nodeData(): WorkflowNodeData {
    return { ...this._data };
  }

  /**
   * Stable display label for this node, used as the prefix in tool-call
   * param bindings (`{displayName}/field/path`).
   */
  get name(): string {
    const record = isRecord(this._data.data) ? this._data.data : {};
    const rawName = record['name'];
    if (typeof rawName === 'string' && rawName.trim() !== '') {
      return rawName;
    }
    return this.defaultDisplayName();
  }

  protected defaultDisplayName(): string {
    return humanizeNodeType(this._data.type);
  }

  /**
   * The node this one runs once per item (a for-each's loop body), or null
   * for a node that doesn't loop or has no body yet.
   */
  loopBodyNodeId(): string | null {
    return null;
  }

  /**
   * ERAQL output shape derived from node type and linked resources (not persisted).
   * When `defaultOutput` is set, its shape is inferred first; subclasses extend via
   * `deriveTypedOutputShape`.
   */
  deriveOutputShape(context: WorkflowNodeOutputShapeContext): string | null {
    const fromDefaultOutput = outputShapeFromDefaultOutput(
      this._data.defaultOutput
    );
    if (fromDefaultOutput !== null) {
      return fromDefaultOutput;
    }
    return this.deriveTypedOutputShape(context);
  }

  protected deriveTypedOutputShape(
    _context: WorkflowNodeOutputShapeContext
  ): string | null {
    return null;
  }

  /**
   * Resources this node's data points at: what sharing the workflow has to
   * share along with it, and what copying it has to repoint.
   */
  resourceRefs(): readonly WorkflowNodeResourceRef[] {
    return [];
  }

  /**
   * `data` with each ref that `remap` gives a new id for swapped in. Returns
   * the original data untouched when nothing changes.
   */
  withResourceRefsRemapped(
    remap: (ref: WorkflowNodeResourceRef) => string | undefined
  ): JsonValue {
    const data = this._data.data;
    const changed = this.resourceRefs().flatMap((ref) => {
      const next = remap(ref);
      return next === undefined || next === ref.id
        ? []
        : [[ref.field, next] as const];
    });
    if (changed.length === 0 || !isJsonObject(data)) {
      return data;
    }
    return { ...data, ...Object.fromEntries(changed) };
  }

  /**
   * Every upstream ref this node reads at run time, wherever its type keeps
   * them. A node whose data doesn't parse reports none.
   */
  upstreamRefs(): readonly WorkflowNodeUpstreamRef[] {
    return [];
  }

  /** Refs in a `{ key: ref }` binding map; a value may be a `{{ref}}` template. */
  protected bindingMapRefs(
    bindings: Readonly<Record<string, string>> | undefined
  ): WorkflowNodeUpstreamRef[] {
    return Object.entries(bindings ?? {}).flatMap(
      ([paramKey, ref]): WorkflowNodeUpstreamRef[] => {
        if (ref.trim() === '') {
          return [];
        }
        // The tokens inside a template are the refs; the text around them isn't.
        if (containsTemplateRef(ref)) {
          return collectTemplateRefs(ref).map((token) => ({
            paramKey,
            ref: token.ref,
            template: true,
          }));
        }
        return [{ paramKey, ref, template: false }];
      }
    );
  }

  serialize(): SerializedWorkflowNode {
    return {
      id: this._data.id,
      ownerId: this._data.ownerId,
      ownerOrgId: this._data.ownerOrgId,
      workflowId: this._data.workflowId,
      type: this._data.type,
      data: this._data.data,
      inputShape: this._data.inputShape,
      defaultOutput: this._data.defaultOutput,
      inputFromNodeId: this._data.inputFromNodeId,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  static fromSerialized(
    _serialized: SerializedWorkflowNode,
    _hydration: WorkflowNodeHydration
  ): WorkflowNode {
    throw new Error(
      'WorkflowNode.fromSerialized is not initialized; import @cotera/sdk/core persisted-workflow workflow-node entry'
    );
  }
}

export async function resolveNoWorkflow(): Promise<Workflow | null> {
  return null;
}
